import { useEffect, useRef } from 'react';
import type { VideoTrack } from 'livekit-client';
import type { VoiceState } from '../voice/VoiceSession';
import { MegaphoneIcon, ScreenIcon } from './icons';
import type { RoomContext } from './RoomView';

function TrackVideo({ track }: { track: VideoTrack }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current!;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);
  return <video ref={ref} autoPlay playsInline muted />;
}

export function Stage({ ctx, screen, announcing }: { ctx: RoomContext; screen: VoiceState['screen']; announcing: boolean }) {
  const frame = useRef<HTMLDivElement>(null);
  const host = ctx.quiz.participants.find((p) => p.id === ctx.quiz.hostId);

  return (
    <section className="stage" ref={frame} aria-label="Shared screen">
      {announcing && (
        <div className="announce-strip" role="status">
          <MegaphoneIcon /> {ctx.isHost ? 'You are announcing to everyone' : `${host?.name ?? 'Quizmaster'} is announcing`}
        </div>
      )}
      {screen ? (
        <>
          <TrackVideo track={screen.track} />
          <button className="btn btn-quiet stage-full" onClick={() => void frame.current?.requestFullscreen?.()}>
            Full screen
          </button>
        </>
      ) : (
        <div className="stage-empty">
          <ScreenIcon width={40} height={40} />
          {ctx.isHost ? (
            <>
              <p className="stage-title">Share your slides to start</p>
              <p>Everyone sees your screen here. Players can keep whispering while it's up.</p>
              <button className="btn btn-primary" onClick={() => void ctx.voice.startScreenShare()}>
                Share screen
              </button>
            </>
          ) : (
            <>
              <p className="stage-title">{ctx.quiz.name}</p>
              <p>The quizmaster's screen will appear here.</p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
